import {
  SUBHEADER_FILL,
  TABLE_HEADER_FILL,
  addQuestionRow,
  addSectionHeader,
  addShortAnswerRow,
  addTextAnswerRow,
  formatNumber,
  formatPercent,
  getFyLabels,
  mergeRow,
  safeText,
  setCell,
  setPrinciple5ColumnWidths,
  toYesNo,
  toYesNoNa,
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  COL_K,
  COL_L,
  COL_M,
  COL_N,
} from "./principle6SheetHelpers";
import { filterPopulatedRows } from "./principle8SheetHelpers";

const PRINCIPLE_TITLE =
  "Businesses should engage with and provide value to their consumers in a responsible manner";

const COMPLAINT_CATEGORIES = [
  ["Data privacy", "dataPrivacy"],
  ["Advertising", "advertising"],
  ["Cyber-security", "cyberSecurity"],
  ["Delivery of essential services", "deliveryEssentialServices"],
  ["Restrictive Trade Practices", "restrictiveTradePractices"],
  ["Unfair Trade Practices", "unfairTradePractices"],
  ["Other", "other"],
];

const TURNOVER_ROWS = [
  [
    "Environmental and social parameters relevant to the product",
    "environmentalAndSocialParameters",
  ],
  ["Safe and responsible usage", "safeAndResponsibleUsage"],
  ["Recycling and/or safe disposal", "recyclingAndSafeDisposal"],
];

const RECALL_ROWS = [
  ["Voluntary recalls", "voluntaryRecalls"],
  ["Forced recalls", "forcedRecalls"],
];

function addConsumerComplaintsTable(ws, row, complaints, fyCurrent, fyPrevious) {
  row = addQuestionRow(
    ws,
    row,
    "3. Number of consumer complaints in respect of the following:",
    "",
    { fullWidth: true }
  );

  mergeRow(ws, row, COL_D, COL_D, "", { fill: TABLE_HEADER_FILL });
  mergeRow(ws, row, COL_E, COL_G, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_H, COL_J, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_E, "Received during the year", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "Pending resolution at end of year", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  setCell(ws, row, COL_G, "Remarks", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "Received during the year", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "Pending resolution at end of year", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  setCell(ws, row, COL_J, "Remarks", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const curr = complaints?.currentFinancialYear || {};
  const prev = complaints?.previousFinancialYear || {};

  COMPLAINT_CATEGORIES.forEach(([label, key]) => {
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatNumber(curr?.[key]?.received), { align: "center" });
    setCell(ws, row, COL_F, formatNumber(curr?.[key]?.pendings), { align: "center" });
    setCell(ws, row, COL_G, safeText(curr?.[key]?.remark, ""), { align: "left" });
    setCell(ws, row, COL_H, formatNumber(prev?.[key]?.received), { align: "center" });
    setCell(ws, row, COL_I, formatNumber(prev?.[key]?.pendings), { align: "center" });
    setCell(ws, row, COL_J, safeText(prev?.[key]?.remark, ""), { align: "left" });
    row += 1;
  });

  return row;
}

function addTurnoverTable(ws, row, turnover) {
  row = addQuestionRow(
    ws,
    row,
    "2. Turnover of products and/ services as a percentage of turnover from all products/service that carry information about:",
    "",
    { fullWidth: true }
  );

  mergeRow(ws, row, COL_D, COL_M, "Parameter", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_N, COL_N, "As a percentage to total turnover", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  TURNOVER_ROWS.forEach(([label, key]) => {
    mergeRow(ws, row, COL_D, COL_M, label, { align: "left" });
    setCell(ws, row, COL_N, formatPercent(turnover?.[key]), { align: "center" });
    row += 1;
  });

  return row;
}

function addProductRecallsTable(ws, row, recalls) {
  row = addQuestionRow(
    ws,
    row,
    "4. Details of instances of product recalls on account of safety issues:",
    "",
    { fullWidth: true }
  );

  mergeRow(ws, row, COL_D, COL_H, "Parameter", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "Number", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_J, COL_N, "Reasons for recall", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  row += 1;

  RECALL_ROWS.forEach(([label, key]) => {
    mergeRow(ws, row, COL_D, COL_H, label, { align: "left" });
    setCell(ws, row, COL_I, formatNumber(recalls?.[key]?.number), { align: "center" });
    mergeRow(ws, row, COL_J, COL_N, safeText(recalls?.[key]?.reasonForRecall, ""), { align: "left" });
    row += 1;
  });

  return row;
}

function addChannelsTable(ws, row, channels) {
  const rows = filterPopulatedRows(channels, ["chennel", "link"]);
  if (!rows.length) return row;

  setCell(ws, row, COL_D, "Sr. No.", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_K, "Channel/Platform Name", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  mergeRow(ws, row, COL_L, COL_N, "Link (If Any)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  rows.forEach((item, idx) => {
    setCell(ws, row, COL_D, String(idx + 1), { align: "center" });
    mergeRow(ws, row, COL_E, COL_K, safeText(item?.chennel), { align: "left" });
    mergeRow(ws, row, COL_L, COL_N, safeText(item?.link, ""), { align: "left" });
    row += 1;
  });

  return row;
}

export function buildPrinciple9Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 9", { views: [{ showGridLines: true }] });
  setPrinciple5ColumnWidths(ws);

  const principle = sectionC?.principle9 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);

  let row = 3;
  row = addSectionHeader(ws, row, `PRINCIPLE 9: ${PRINCIPLE_TITLE}`);
  row = addSectionHeader(ws, row, "Essential Indicators", SUBHEADER_FILL);

  row = addTextAnswerRow(
    ws,
    row,
    "1. Describe the mechanisms in place to receive and respond to consumer complaints and feedback.",
    essential?.consumerComplaintsMechanisms
  );

  row = addTurnoverTable(ws, row, essential?.turnoverPercentageWithInformation);
  row = addConsumerComplaintsTable(ws, row, essential?.consumerComplaints, fyCurrent, fyPrevious);
  row = addProductRecallsTable(ws, row, essential?.productRecalls);

  row = addShortAnswerRow(
    ws,
    row,
    "5. Does the entity have a framework/ policy on cyber security and risks related to data privacy? (Yes/No)",
    toYesNo(essential?.cyberSecurityAndDataPrivacyPolicy?.action)
  );
  row = addTextAnswerRow(
    ws,
    row,
    "If available, provide a web-link of the policy.",
    toYesNo(essential?.cyberSecurityAndDataPrivacyPolicy?.action) === "Yes"
      ? essential?.cyberSecurityAndDataPrivacyPolicy?.webLink
      : ""
  );

  row = addTextAnswerRow(
    ws,
    row,
    "6. Provide details of any corrective actions taken or underway on issues relating to advertising, and delivery of essential services; cyber security and data privacy of customers; re-occurrence of instances of product recalls; penalty / action taken by regulatory authorities on safety of products / services.",
    essential?.correctiveActions
  );

  row = addQuestionRow(
    ws,
    row,
    "7. Provide the following information relating to data breaches:",
    "",
    { fullWidth: true }
  );
  row = addShortAnswerRow(
    ws,
    row,
    "a. Number of instances of data breaches along-with impact",
    formatNumber(essential?.dataBreachDetails?.instancesOfDataBreaches)
  );
  row = addShortAnswerRow(
    ws,
    row,
    "b. Percentage of data breaches involving personally identifiable information of customers",
    formatPercent(essential?.dataBreachDetails?.percentageInvolvingPersonalData)
  );
  row = addTextAnswerRow(
    ws,
    row,
    "c. Impact, if any, of the data breaches",
    essential?.dataBreachDetails?.impactOfDataBreaches
  );

  row = addSectionHeader(ws, row, "Leadership Indicators", SUBHEADER_FILL);

  row = addQuestionRow(
    ws,
    row,
    "1. Channels / platforms where information on products and services of the entity can be accessed (provide web link, if available).",
    "",
    { fullWidth: true }
  );
  row = addChannelsTable(ws, row, leadership?.channelsForProductInformation);

  row = addTextAnswerRow(
    ws,
    row,
    "2. Steps taken to inform and educate consumers about safe and responsible usage of products and/or services.",
    leadership?.consumerEducation
  );

  row = addTextAnswerRow(
    ws,
    row,
    "3. Mechanisms in place to inform consumers of any risk of disruption / discontinuation of essential services.",
    leadership?.riskDisclosureMechanisms
  );

  row = addShortAnswerRow(
    ws,
    row,
    "4. Does the entity display product information on the product over and above what is mandated as per local laws? (Yes / No / Not Applicable)",
    toYesNoNa(leadership?.productInformationDisplayed?.action)
  );
  row = addTextAnswerRow(
    ws,
    row,
    "If yes, provide details in brief.",
    String(leadership?.productInformationDisplayed?.action) === "true"
      ? leadership?.productInformationDisplayed?.remark
      : ""
  );

  row = addShortAnswerRow(
    ws,
    row,
    "5. Did your entity carry out any survey with regard to consumer satisfaction relating to the major products / services of the entity, significant locations of operation of the entity or the entity as a whole? (Yes/No)",
    toYesNo(leadership?.consumerSatisfactionSurvey?.action)
  );
  row = addTextAnswerRow(
    ws,
    row,
    "If yes, provide details in brief.",
    leadership?.consumerSatisfactionSurvey?.action
      ? leadership?.consumerSatisfactionSurvey?.remark
      : ""
  );

  return ws;
}
